import { useState, useEffect, useMemo, useCallback } from "react";
import { Loader2, BarChart3, Users, Globe, MapPin, Calendar, Search, Building, X as XIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart
} from "recharts";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const PERIOD_OPTIONS = [
  { value: "month",   label: "Mese corrente" },
  { value: "quarter", label: "Ultimo trimestre" },
  { value: "half",    label: "Ultimo semestre" },
  { value: "all",     label: "Tutto" },
];

const STATUS_COLORS = {
  new:         { bg: "bg-emerald-500", label: "Nuovi" },
  interesting: { bg: "bg-blue-500",    label: "Interessanti" },
  contacted:   { bg: "bg-amber-500",   label: "Contattati" },
  acquired:    { bg: "bg-yellow-400",  label: "Acquisiti" },
  discarded:   { bg: "bg-zinc-300",    label: "Scartati" },
};

function BarH({ value, max, color = "bg-zinc-900" }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="h-5 w-full bg-zinc-100 rounded-sm overflow-hidden">
      <div className={`h-full ${color} rounded-sm transition-all duration-500`} style={{ width: `${pct}%` }} />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, color = "text-zinc-900" }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-4">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] tracking-wider uppercase font-semibold text-zinc-400">{label}</span>
        <Icon className={`w-3.5 h-3.5 ${color}`} />
      </div>
      <div className={`text-2xl font-black tabular-nums ${color}`}>{value}</div>
      {sub && <div className="text-[10px] text-zinc-400 mt-0.5">{sub}</div>}
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-zinc-900 text-white text-xs px-3 py-2 rounded-sm shadow-lg">
        <p className="font-bold mb-0.5">{label}</p>
        <p>{payload[0].value} immobili</p>
      </div>
    );
  }
  return null;
};

export default function StatisticsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("all");
  const [zoneSearch, setZoneSearch] = useState("");
  const [portalSearch, setPortalSearch] = useState("");

  const fetchStats = useCallback((p) => {
    setLoading(true);
    axios
      .get(`${API}/statistics?period=${p}`)
      .then(r => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { fetchStats(period); }, [period, fetchStats]);

  const filteredZones = useMemo(() => {
    if (!data) return [];
    return data.zones.filter(z => z.zone.toLowerCase().includes(zoneSearch.toLowerCase()));
  }, [data, zoneSearch]);

  const filteredPortals = useMemo(() => {
    if (!data) return [];
    return data.portals.filter(p => p.portal.toLowerCase().includes(portalSearch.toLowerCase()));
  }, [data, portalSearch]);

  const maxAgent  = data ? Math.max(...data.agents.map(a => a.total), 1) : 1;
  const maxPortal = data ? Math.max(...data.portals.map(p => p.count), 1) : 1;
  const maxZone   = data ? Math.max(...data.zones.map(z => z.count), 1) : 1;
  const totalRes  = data?.categories?.residenziale || 0;
  const totalComm = data?.categories?.commerciale  || 0;

  return (
    <div data-testid="statistics-page" className="p-4 sm:p-6 space-y-6">
      {/* Header + filtro periodo */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="font-heading text-2xl sm:text-4xl font-black text-zinc-900 tracking-tighter">Statistiche</h1>
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-zinc-400" />
          <Select value={period} onValueChange={v => { setPeriod(v); }}>
            <SelectTrigger data-testid="period-filter" className="h-8 w-44 rounded-sm border-zinc-300 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PERIOD_OPTIONS.map(o => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {loading && <Loader2 className="w-4 h-4 animate-spin text-zinc-400" />}
        </div>
      </div>

      {loading && !data && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-5 h-5 animate-spin text-zinc-400" />
        </div>
      )}

      {!loading && !data && (
        <div className="p-6 text-zinc-500">Errore caricamento statistiche</div>
      )}

      {data && (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard icon={Building} label="Totale immobili" value={data.total} />
            <StatCard icon={Building} label="Residenziale" value={totalRes} color="text-teal-700" />
            <StatCard icon={Building} label="Commerciale" value={totalComm} color="text-indigo-700" />
            <StatCard icon={Globe} label="Portali attivi" value={data.portals.length} color="text-emerald-700" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Per Agente */}
            <div className="bg-white border border-zinc-200 rounded-sm p-4">
              <div className="flex items-center gap-2 mb-4">
                <Users className="w-4 h-4 text-zinc-500" />
                <h2 className="text-sm font-bold text-zinc-900 uppercase tracking-wider">Per Agente</h2>
              </div>
              <div className="space-y-3">
                {data.agents.map(a => (
                  <div key={a.agent}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-semibold text-zinc-800">{a.agent}</span>
                      <div className="flex items-center gap-2">
                        {a.views > 0 && (
                          <span className="text-[10px] text-zinc-400 font-semibold">
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400 mr-0.5" />
                            {a.views} vis.
                          </span>
                        )}
                        <span className="text-sm font-black tabular-nums text-zinc-900">{a.total}</span>
                      </div>
                    </div>
                    <div className="h-5 w-full bg-zinc-100 rounded-sm overflow-hidden flex">
                      {["new", "interesting", "contacted", "acquired", "discarded"].map(s => {
                        const val = a[s] || 0;
                        const pct = a.total > 0 ? (val / maxAgent) * 100 : 0;
                        return val > 0 ? (
                          <div key={s} className={`h-full ${STATUS_COLORS[s].bg} transition-all`} style={{ width: `${pct}%` }} title={`${STATUS_COLORS[s].label}: ${val}`} />
                        ) : null;
                      })}
                    </div>
                    <div className="flex gap-3 mt-1 flex-wrap">
                      {["new", "interesting", "contacted", "acquired", "discarded"].map(s => (
                        a[s] > 0 && (
                          <span key={s} className="text-[9px] text-zinc-400">
                            <span className={`inline-block w-1.5 h-1.5 rounded-full ${STATUS_COLORS[s].bg} mr-0.5`} />
                            {STATUS_COLORS[s].label}: {a[s]}
                          </span>
                        )
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Per Portale */}
            <div className="bg-white border border-zinc-200 rounded-sm p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-zinc-500" />
                  <h2 className="text-sm font-bold text-zinc-900 uppercase tracking-wider">Per Portale</h2>
                </div>
                <div className="relative w-36">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-zinc-400" />
                  <Input value={portalSearch} onChange={e => setPortalSearch(e.target.value)} placeholder="Cerca..." className="h-7 text-xs pl-7 rounded-sm border-zinc-200" />
                </div>
              </div>
              <div className="space-y-2">
                {filteredPortals.map(p => (
                  <div key={p.portal} className="flex items-center gap-3">
                    <span className="text-xs font-medium text-zinc-700 w-44 truncate shrink-0">{p.portal}</span>
                    <BarH value={p.count} max={maxPortal} color="bg-emerald-500" />
                    <span className="text-xs font-bold tabular-nums text-zinc-900 w-8 text-right shrink-0">{p.count}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Trend Settimanale — LineChart */}
            <div className="bg-white border border-zinc-200 rounded-sm p-4">
              <div className="flex items-center gap-2 mb-4">
                <BarChart3 className="w-4 h-4 text-zinc-500" />
                <h2 className="text-sm font-bold text-zinc-900 uppercase tracking-wider">Trend Settimanale</h2>
                <span className="text-[10px] text-zinc-400">
                  {PERIOD_OPTIONS.find(o => o.value === period)?.label}
                </span>
              </div>
              {data.periods.length === 0 ? (
                <p className="text-xs text-zinc-400 italic">Nessun dato per il periodo selezionato</p>
              ) : (
                <ResponsiveContainer width="100%" height={180}>
                  <AreaChart data={data.periods} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="trendGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor="#3b82f6" stopOpacity={0.18} />
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 10, fill: "#a1a1aa" }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      allowDecimals={false}
                      tick={{ fontSize: 10, fill: "#a1a1aa" }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Area
                      type="monotone"
                      dataKey="count"
                      stroke="#3b82f6"
                      strokeWidth={2}
                      fill="url(#trendGrad)"
                      dot={{ r: 3, fill: "#3b82f6", strokeWidth: 0 }}
                      activeDot={{ r: 5, fill: "#1d4ed8" }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Per Tipo Immobile */}
            <div className="bg-white border border-zinc-200 rounded-sm p-4">
              <div className="flex items-center gap-2 mb-4">
                <Building className="w-4 h-4 text-zinc-500" />
                <h2 className="text-sm font-bold text-zinc-900 uppercase tracking-wider">Per Tipologia</h2>
              </div>
              <div className="space-y-2">
                {data.property_types.map(t => (
                  <div key={t.type} className="flex items-center gap-3">
                    <span className="text-xs font-medium text-zinc-700 w-32 truncate shrink-0">{t.type}</span>
                    <BarH value={t.count} max={data.property_types[0]?.count || 1} color="bg-indigo-500" />
                    <span className="text-xs font-bold tabular-nums text-zinc-900 w-8 text-right shrink-0">{t.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Zone — full width */}
          <div className="bg-white border border-zinc-200 rounded-sm p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-zinc-500" />
                <h2 className="text-sm font-bold text-zinc-900 uppercase tracking-wider">Per Zona / Comune</h2>
                <span className="text-[10px] text-zinc-400">{filteredZones.length} zone</span>
              </div>
              <div className="relative w-48">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-zinc-400" />
                <Input value={zoneSearch} onChange={e => setZoneSearch(e.target.value)} placeholder="Cerca zona..." className="h-7 text-xs pl-7 rounded-sm border-zinc-200" />
                {zoneSearch && (
                  <button onClick={() => setZoneSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600">
                    <XIcon className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {filteredZones.map(z => (
                <div key={z.zone} className="flex items-center gap-2">
                  <span className="text-[11px] text-zinc-600 w-48 truncate shrink-0" title={z.zone}>{z.zone}</span>
                  <BarH value={z.count} max={maxZone} color="bg-amber-500" />
                  <span className="text-xs font-bold tabular-nums text-zinc-700 w-6 text-right shrink-0">{z.count}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
